/**
 * Sponsored Results — pilot section rendered above organic listings on
 * /pet-sitters/{city} pages. Renders nothing when no sponsor is
 * eligible for the requested city (per the pilot spec — no empty
 * state, just don't show the band at all).
 *
 * Data flow:
 *   1. Fetch GET /api/sponsors?city=&state=&category=pet-sitter
 *   2. If sponsors.length === 0 → return null (section hidden).
 *   3. Otherwise render the "Sponsored Results" band with a card
 *      per sponsor.
 *
 * Card clickability:
 *   The whole card is clickable — an absolutely-positioned Link
 *   overlay routes card-body clicks to the sponsor's `linked_place_url`
 *   (existing internal place page). The action buttons (Visit Website,
 *   Call) sit above the overlay with `relative z-10` so they keep
 *   their own outbound behaviour. When a sponsor has no
 *   `linked_place_url`, we fall back to making the card-body a
 *   trigger for the outbound website (same click tracking as the
 *   Visit Website button).
 *
 * Outbound clicks:
 *   POST /api/sponsors/{slug}/click returns the destination URL with
 *   utm_source=pawground&utm_medium=sponsored_pilot appended. Frontend
 *   awaits that response before navigating so the click is counted
 *   even if the target site is slow to open.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Info } from "lucide-react";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Mirror the same absolute-path vs. photo-ref resolution used by
// ParkCard/ParkDetail so a sponsor's photo_url can be either a local
// asset (`/images/...`) or a Google Places ref (`AVoNoX...`) — the
// sponsor endpoint now returns whichever the linked place record has
// for its first photo (see routes/sponsors.py::_photo_from_linked_place).
const resolvePhotoUrl = (ref, w = 240) => {
  if (!ref) return null;
  if (ref.startsWith("http") || ref.startsWith("/")) return ref;
  return `${BACKEND_URL}/api/photo?ref=${encodeURIComponent(ref)}&w=${w}`;
};

// Fallback silhouette shown when a sponsor has no photo_url on file.
// Kept as a component so both the real <img> and the placeholder occupy
// the same 96×96 square with the same rounded corners.
const PawSilhouette = () => (
  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#6B8E5A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="4" r="2" /><circle cx="18" cy="8" r="2" /><circle cx="4" cy="8" r="2" /><circle cx="7" cy="17" r="2" /><circle cx="15" cy="17" r="2" />
    <path d="M12 12c-2.5 0-4 2-4 4 0 3 2.5 4 4 4s4-1 4-4c0-2-1.5-4-4-4z" />
  </svg>
);

const SponsorCard = ({ sponsor }) => {
  const {
    slug, name, quote, tags = [], rating, rating_source,
    phone, phone_display, website_url, linked_place_url, photo_url,
  } = sponsor;

  const serviceAreaText = sponsor.service_area_text || "";

  const trackAndOpenWebsite = async () => {
    try {
      const r = await api.post(`/sponsors/${slug}/click`, {
        referrer_path: window.location.pathname,
        click_source: "band",
      });
      const url = r?.data?.url || website_url;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) {
      window.open(website_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleVisit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    trackAndOpenWebsite();
  };

  // Card-wide click behaviour: prefer the internal deep-link when we
  // have one (photo + reviews already there); otherwise fall back to
  // opening the outbound website with the same click tracking as the
  // "Visit Website" button.
  const hasInternalLink = Boolean(linked_place_url);

  return (
    <article
      data-testid={`sponsor-card-${slug}`}
      className="relative bg-white rounded-2xl p-5 border flex gap-4 items-start transition-shadow hover:shadow-warm"
      style={{ borderColor: "#D4C9B0" }}
    >
      {/* Card-wide clickable overlay. Sits below the action buttons
          (which have relative z-10) so those keep their own handlers. */}
      {hasInternalLink ? (
        <Link
          to={linked_place_url}
          data-testid={`sponsor-card-link-${slug}`}
          aria-label={`View ${name} details`}
          className="absolute inset-0 rounded-2xl z-0"
        />
      ) : (
        <button
          type="button"
          onClick={trackAndOpenWebsite}
          data-testid={`sponsor-card-link-${slug}`}
          aria-label={`Visit ${name} website`}
          className="absolute inset-0 rounded-2xl z-0 cursor-pointer bg-transparent"
        />
      )}

      <div
        className="relative z-[1] flex-shrink-0 rounded-xl overflow-hidden pointer-events-none"
        style={{ width: 96, height: 96, backgroundColor: "#E8F2EC" }}
        aria-hidden="true"
      >
        {photo_url ? (
          <img
            src={resolvePhotoUrl(photo_url, 240)}
            alt=""
            width={96}
            height={96}
            loading="lazy"
            className="w-full h-full object-cover"
            data-testid={`sponsor-photo-${slug}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PawSilhouette />
          </div>
        )}
      </div>

      <div className="relative z-[1] flex-1 min-w-0 pointer-events-none">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-bold text-lg" style={{ color: "#1A1A0F" }}>{name}</h3>
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ color: "#B85C38", backgroundColor: "#FBEEE4", letterSpacing: "0.08em" }}
          >Sponsored</span>
        </div>

        {rating != null && (
          <div className="flex items-center gap-1 mt-1" data-testid={`sponsor-rating-${slug}`}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="w-4 h-4" fill={i < Math.round(rating) ? "#F5A623" : "none"} stroke="#F5A623" strokeWidth={1.5} />
            ))}
            <span className="text-sm ml-1" style={{ color: "#4a4640" }}>
              {rating.toFixed(1)}{rating_source ? ` (${rating_source})` : ""}
            </span>
          </div>
        )}

        {serviceAreaText && (
          <p className="text-sm mt-1" style={{ color: "#4a4640" }}>{serviceAreaText}</p>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {tags.map((t) => (
              <span
                key={t}
                className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ backgroundColor: "#E8F2EC", color: "#2F5A3F" }}
              >{t}</span>
            ))}
          </div>
        )}

        {quote && (
          <blockquote
            className="mt-3 pl-3 italic text-sm"
            style={{ borderLeft: "3px solid #6B8E5A", color: "#4a4640" }}
          >“{quote}”</blockquote>
        )}

        {/* Action buttons keep pointerEvents auto + z-10 so they win
            over the card-wide overlay above. */}
        <div className="flex flex-wrap gap-2 mt-3 pointer-events-auto relative z-10">
          <a
            href={website_url}
            onClick={handleVisit}
            data-testid={`sponsor-visit-${slug}`}
            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "#4A7C59" }}
          >Visit Website</a>
          {phone && (
            <a
              href={`tel:${phone}`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`sponsor-call-${slug}`}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border hover:bg-white/50 transition-colors"
              style={{ borderColor: "#D4C9B0", color: "#1A1A0F" }}
            >{phone_display ? `Call ${phone_display}` : "Call"}</a>
          )}
        </div>
      </div>
    </article>
  );
};

const SponsoredResults = ({ city, state, categoryKey }) => {
  const [sponsors, setSponsors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!city || !state || !categoryKey) return;
    let cancelled = false;
    api
      .get(`/sponsors?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&category=${encodeURIComponent(categoryKey)}`)
      .then((r) => {
        if (cancelled) return;
        setSponsors(r?.data?.sponsors || []);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSponsors([]);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [city, state, categoryKey]);

  // Renders nothing when no eligible sponsor — per pilot spec, hide
  // the whole band, don't show an empty state.
  if (!loaded || sponsors.length === 0) return null;

  return (
    <section
      data-testid="sponsored-results"
      className="rounded-2xl p-5 mb-6"
      style={{ backgroundColor: "#FBEEE4", border: "1px solid #E8D9C4" }}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-1.5">
          <h2 className="font-heading font-bold text-lg" style={{ color: "#B85C38" }}>
            Sponsored Results
          </h2>
          <span title="Local businesses featured here" aria-label="About sponsored results">
            <Info className="w-4 h-4" style={{ color: "#B85C38" }} />
          </span>
        </div>
        <span className="text-xs" style={{ color: "#7A7A60" }}>
          Local businesses featured here
        </span>
      </div>
      <div className="space-y-3">
        {sponsors.map((s) => <SponsorCard key={s.slug} sponsor={s} />)}
      </div>
    </section>
  );
};

export default SponsoredResults;
